import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getTodosClientes, logoutUsuario, obtenerHorasReporteCliente, obtenerHorasReporteTodosClientes } from '../../actions/index.js';
import NavBarHome from '../NavBarHome/NavBarHome.jsx';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner.jsx';
import Select from 'react-select';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LabelList
} from 'recharts';
import './ReporteClientes.css';

// Paleta de colores para los gráficos (verde corporativo + variantes)
const CHART_COLORS = ['#28C76F', '#3FE07F', '#1E9E75', '#6CECA0', '#8A988F'];

// react-select con la paleta moderna oscura + verde
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#0A100D',
    borderColor: state.isFocused ? 'rgba(40,199,111,0.6)' : 'rgba(255,255,255,0.10)',
    borderWidth: '0.5px',
    borderRadius: '10px',
    color: '#E8EFEA',
    minHeight: '42px',
    height: '42px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(40,199,111,0.20)' : 'none',
    '&:hover': { borderColor: 'rgba(40,199,111,0.45)' }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#0C120F',
    border: '0.5px solid rgba(40,199,111,0.25)',
    borderRadius: '11px',
    overflow: 'hidden',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
    zIndex: 9999,
  }),
  menuList: (provided) => ({ ...provided, padding: '6px', maxHeight: '230px' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'rgba(40,199,111,0.16)' : state.isFocused ? 'rgba(40,199,111,0.12)' : 'transparent',
    color: state.isSelected || state.isFocused ? '#EAF6EF' : '#CBD6CF',
    borderRadius: '8px',
    padding: '9px 11px',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  placeholder: (provided) => ({ ...provided, color: '#9FB0A6', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  input: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13 }),
  valueContainer: (provided) => ({ ...provided, backgroundColor: 'transparent', flexWrap: 'nowrap', height: '42px', padding: '0 10px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided, state) => ({ ...provided, color: state.isFocused ? '#3FE07F' : '#5C6862', '&:hover': { color: '#3FE07F' } }),
  clearIndicator: (provided) => ({ ...provided, color: '#9FB0A6', '&:hover': { color: '#E86D6D' } }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const tooltipStyle = {
  background: '#0C120F',
  border: '0.5px solid rgba(40,199,111,0.3)',
  borderRadius: 10,
  color: '#E8EFEA',
  fontSize: 13,
};

const ReporteClientes = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Selectors de Redux
  const user = useSelector(state => state.usuario);
  const clientes = useSelector(state => state.clientesReporte);
  const reporteCliente = useSelector(state => state.reporteCliente);
  
  // Estados para filtros
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  
  // Estados para datos
  const [horasConsultadas, setHorasConsultadas] = useState([]);

  // useEffect para cargar datos iniciales
  useEffect(() => {
    dispatch(getTodosClientes());
  }, [dispatch]);

  // Función para manejar el cierre de sesión
  const handleLogout = () => {
    dispatch(logoutUsuario());
    history.push('/login');
  };

  // Función para formatear fecha para mostrar
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const [dia, mes, año] = fecha.split('/');
    return `${dia}/${mes}/${año}`;
  };

  // Función para formatear horas
  const formatearHoras = (horas) => {
    return parseFloat(horas).toFixed(1);
  };

  // Preparar opciones para el select de clientes
  const opcionesClientes = clientes.map(cliente => ({
    value: cliente.id,
    label: cliente.nombre
  }));

  // Función para exportar datos detallados a Excel
  const exportarAExcel = () => {
    if (horasConsultadas.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Preparar datos para Excel
    const datosExcel = horasConsultadas.map(hora => ({
      Fecha: formatearFecha(hora.fechaCarga),
      Cliente: hora.cliente ? hora.cliente.nombre : 'Sin cliente',
      Usuario: hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido',
      Seniority: hora.senorityhora || 'N/A',
      Servicio: hora.servicio ? hora.servicio.nombre : 'Sin servicio',
      Tarea: hora.tarea ? hora.tarea.nombre : 'Sin tarea',
      Descripción: hora.descripcion || 'Sin descripción',
      Horas: parseFloat(hora.horas)
    }));

    // Crear libro de Excel
    const ws = utils.json_to_sheet(datosExcel);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Detalle Horas Cliente');

    // Guardar archivo
    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    
    function s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
      return buf;
    }
    
    const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
    const clienteNombre = clienteSeleccionado ? clienteSeleccionado.label : 'todos_clientes';
    saveAs(blob, `reporte_cliente_${clienteNombre}_${new Date().getTime()}.xlsx`);
  };

  // Función para exportar resumen de usuarios a Excel
  const exportarResumenUsuariosAExcel = () => {
    const resumenUsuarios = obtenerResumenUsuarios();
    if (resumenUsuarios.length === 0) {
      alert('No hay datos de resumen para exportar');
      return;
    }

    const ws = utils.json_to_sheet(resumenUsuarios);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Resumen por Usuario');

    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    
    function s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xFF;
      return buf;
    }
    
    const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
    const clienteNombre = clienteSeleccionado ? clienteSeleccionado.label : 'todos_clientes';
    saveAs(blob, `resumen_usuarios_${clienteNombre}_${new Date().getTime()}.xlsx`);
  };

  // Función para obtener resumen de usuarios
  const obtenerResumenUsuarios = () => {
    const resumenMap = {};
    const totalHoras = horasConsultadas.reduce((total, hora) => total + parseFloat(hora.horas), 0);
    
    horasConsultadas.forEach(hora => {
      const usuarioKey = hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido';
      const seniority = hora.senorityhora || 'N/A';
      
      if (!resumenMap[usuarioKey]) {
        resumenMap[usuarioKey] = {
          'Usuario': usuarioKey,
          'Seniority': seniority,
          'Total Horas': 0,
          'Registros': 0,
          'Porcentaje': 0
        };
      }
      
      resumenMap[usuarioKey]['Total Horas'] += parseFloat(hora.horas);
      resumenMap[usuarioKey]['Registros'] += 1;
    });
    
    // Calcular porcentajes
    Object.values(resumenMap).forEach(usuario => {
      usuario['Porcentaje'] = totalHoras > 0 ? 
        ((usuario['Total Horas'] / totalHoras) * 100).toFixed(1) + '%' : 
        '0%';
    });
    
    return Object.values(resumenMap).sort((a, b) => b['Total Horas'] - a['Total Horas']);
  };

  // Función para limpiar filtros
  const limpiarFiltros = () => {
    setClienteSeleccionado(null);
    setFechaInicio(null);
    setFechaFin(null);
    setHorasConsultadas([]);
  };

  // Función para buscar horas
  const buscarHoras = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Debe seleccionar un rango de fechas');
      return;
    }

    // Convertir fechas a formato YYYY-MM-DD para el backend
    const fechaInicioStr = fechaInicio.toISOString().split('T')[0];
    const fechaFinStr = fechaFin.toISOString().split('T')[0];

    try {
      let horasData = [];
      
      if (clienteSeleccionado) {
        // Buscar horas de un cliente específico
        console.log('🔍 Buscando horas del cliente:', clienteSeleccionado.value, 'fechas:', fechaInicioStr, 'a', fechaFinStr);
        horasData = await dispatch(obtenerHorasReporteCliente(clienteSeleccionado.value, fechaInicioStr, fechaFinStr));
      } else {
        // Buscar horas de todos los clientes
        console.log('🔍 Buscando horas de todos los clientes, fechas:', fechaInicioStr, 'a', fechaFinStr);
        horasData = await dispatch(obtenerHorasReporteTodosClientes(fechaInicioStr, fechaFinStr));
      }
      
      console.log('✅ Datos obtenidos del backend:', horasData?.length || 0, 'registros');
      setHorasConsultadas(horasData || []);
      
    } catch (error) {
      console.error('❌ Error al buscar horas:', error);
      alert('Error al buscar las horas: ' + (error.message || 'Error desconocido'));
      setHorasConsultadas([]);
    }
  };

  // ===== Datos derivados para métricas y gráficos =====
  const totalHoras = useMemo(() => horasConsultadas.reduce((t, h) => t + parseFloat(h.horas || 0), 0), [horasConsultadas]);
  const registros = horasConsultadas.length;
  const clientesUnicos = useMemo(() => new Set(horasConsultadas.map(h => (h.cliente ? h.cliente.nombre : 'Sin cliente'))).size, [horasConsultadas]);
  const usuariosUnicos = useMemo(() => new Set(horasConsultadas.map(h => (h.usuario ? `${h.usuario.nombre} ${h.usuario.apellidos}` : 'N/A'))).size, [horasConsultadas]);

  const horasPorCliente = useMemo(() => {
    const m = {};
    horasConsultadas.forEach(h => {
      const k = h.cliente ? h.cliente.nombre : 'Sin cliente';
      m[k] = (m[k] || 0) + parseFloat(h.horas || 0);
    });
    return Object.entries(m).map(([nombre, horas]) => ({ nombre, horas: Math.round(horas * 100) / 100 })).sort((a, b) => b.horas - a.horas).slice(0, 8);
  }, [horasConsultadas]);

  const horasPorUsuario = useMemo(() => {
    const m = {};
    horasConsultadas.forEach(h => {
      const k = h.usuario ? `${h.usuario.nombre} ${h.usuario.apellidos}` : 'N/A';
      m[k] = (m[k] || 0) + parseFloat(h.horas || 0);
    });
    const arr = Object.entries(m).map(([nombre, horas]) => ({ nombre, horas: Math.round(horas * 100) / 100 })).sort((a, b) => b.horas - a.horas);
    if (arr.length <= 5) return arr;
    const top = arr.slice(0, 4);
    const otros = arr.slice(4).reduce((t, x) => t + x.horas, 0);
    return [...top, { nombre: 'Otros', horas: Math.round(otros * 100) / 100 }];
  }, [horasConsultadas]);

  // Acorta etiquetas largas para el eje X
  const abreviar = (v) => { const s = String(v); return s.length > 12 ? s.slice(0, 11) + '…' : s; };

  return (
    <div className="reporte-clientes-container">
      <NavBarHome
        user={user}
        handleLogout={handleLogout}
      />
      
      <div className="reporte-clientes-main-content">
        <div className="reporte-clientes-header">
          <h1>Reporte por cliente</h1>
          <p>Consultá y analizá las horas trabajadas por cliente en un período.</p>
        </div>

        <div className="reporte-clientes-filtros-container">
          <div className="reporte-clientes-filtros-layout">
            <div className="reporte-clientes-form-group">
              <label>Cliente (opcional)</label>
              <Select
                styles={customSelectStyles}
                value={clienteSeleccionado}
                onChange={setClienteSeleccionado}
                options={opcionesClientes}
                placeholder="Todos los clientes"
                isClearable
                isSearchable
                className="reporte-clientes-select-cliente"
                classNamePrefix="rep-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="reporte-clientes-form-group">
              <label>Fecha inicio</label>
              <div className="reporte-clientes-datepicker-wrapper">
                <DatePicker
                  selected={fechaInicio}
                  onChange={(date) => setFechaInicio(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecciona fecha inicio"
                  showPopperArrow={false}
                  autoComplete="off"
                  maxDate={new Date()}
                  popperPlacement="bottom-start"
                  popperModifiers={{
                    preventOverflow: {
                      enabled: true,
                      escapeWithReference: false,
                      boundariesElement: 'viewport'
                    }
                  }}
                  fixedHeight
                />
              </div>
            </div>

            <div className="reporte-clientes-form-group">
              <label>Fecha fin</label>
              <div className="reporte-clientes-datepicker-wrapper">
                <DatePicker
                  selected={fechaFin}
                  onChange={(date) => setFechaFin(date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecciona fecha fin"
                  showPopperArrow={false}
                  autoComplete="off"
                  maxDate={new Date()}
                  minDate={fechaInicio}
                />
              </div>
            </div>

            <div className="reporte-clientes-botones-accion">
              <button
                className="reporte-clientes-btn-buscar"
                onClick={buscarHoras}
                disabled={!fechaInicio || !fechaFin}
              >
                Buscar
              </button>
              <button
                className="reporte-clientes-btn-limpiar"
                onClick={limpiarFiltros}
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Resultados */}
        {horasConsultadas.length > 0 && (
          <div className="reporte-clientes-resultados-container">
            {/* Métricas */}
            <div className="rep-metrics">
              <div className="rep-metric"><span className="rep-metric-lbl">Total de horas</span><span className="rep-metric-val green">{Math.round(totalHoras * 100) / 100} h</span><span className="rep-metric-sub">en el período</span></div>
              <div className="rep-metric"><span className="rep-metric-lbl">Registros</span><span className="rep-metric-val">{registros}</span><span className="rep-metric-sub">cargas de horas</span></div>
              <div className="rep-metric"><span className="rep-metric-lbl">Clientes</span><span className="rep-metric-val">{clientesUnicos}</span><span className="rep-metric-sub">con actividad</span></div>
              <div className="rep-metric"><span className="rep-metric-lbl">Usuarios</span><span className="rep-metric-val">{usuariosUnicos}</span><span className="rep-metric-sub">involucrados</span></div>
            </div>

            {/* Gráficos */}
            <div className="rep-charts">
              <div className="rep-card">
                <p className="rep-chart-title">Horas por cliente</p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={horasPorCliente} margin={{ top: 24, right: 8, left: -8, bottom: 4 }} barCategoryGap="25%">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="nombre" interval={0} tickFormatter={abreviar} tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} />
                    <YAxis tick={{ fill: '#8A988F', fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: 'rgba(40,199,111,0.08)' }} contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v) => [`${v} h`, 'Horas']} />
                    <Bar dataKey="horas" fill="#28C76F" radius={[6, 6, 0, 0]} maxBarSize={56}>
                      <LabelList dataKey="horas" position="top" fill="#3FE07F" fontSize={12} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rep-card">
                <p className="rep-chart-title">Distribución por usuario</p>
                <div className="rep-donut-row">
                  <div className="rep-donut-chart">
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={horasPorUsuario} dataKey="horas" nameKey="nombre" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={2} stroke="none">
                          {horasPorUsuario.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#E8EFEA' }} labelStyle={{ color: '#9fb0a6' }} formatter={(v, n) => [`${v} h`, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="rep-legend">
                    {horasPorUsuario.map((e, i) => (
                      <div className="rep-legend-li" key={i}>
                        <span className="rep-legend-dot" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}></span>
                        <span className="rep-legend-name">{e.nombre}</span>
                        <span className="rep-legend-pct">{totalHoras ? Math.round((e.horas / totalHoras) * 100) : 0}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla de resumen por usuario */}
            <div className="reporte-clientes-tabla-container">
              <div className="reporte-clientes-tabla-header">
                <h3>
                  Resumen por usuario
                  {(clienteSeleccionado || fechaInicio || fechaFin) && (
                    <span className="reporte-clientes-filtros-descripcion">
                      {clienteSeleccionado && ` - Cliente: ${clienteSeleccionado.label}`}
                      {fechaInicio && fechaFin && ` - Período: ${fechaInicio.toLocaleDateString()} a ${fechaFin.toLocaleDateString()}`}
                    </span>
                  )}
                </h3>
                <button 
                  className="reporte-clientes-btn-exportar"
                  onClick={exportarResumenUsuariosAExcel}
                >
                  Exportar resumen
                </button>
              </div>
              
              <div className="reporte-clientes-tabla-wrapper">
                <table className="reporte-clientes-tabla-resumen">
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Seniority</th>
                      <th>Total Horas</th>
                      <th>Registros</th>
                      <th>% del Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obtenerResumenUsuarios().map((resumen, index) => (
                      <tr key={index}>
                        <td>{resumen.Usuario}</td>
                        <td>{resumen.Seniority}</td>
                        <td className="reporte-clientes-horas-cell">{formatearHoras(resumen['Total Horas'])}</td>
                        <td>{resumen.Registros}</td>
                        <td>
                          <span className={`reporte-clientes-porcentaje-badge ${resumen.Porcentaje === '100.0%' ? 'completo' : ''}`}>
                            {resumen.Porcentaje}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td></td>
                      <td></td>
                      <td className="reporte-clientes-horas-cell">
                        {formatearHoras(horasConsultadas.reduce((total, hora) => total + parseFloat(hora.horas), 0))}
                      </td>
                      <td>{horasConsultadas.length}</td>
                      <td>
                        <span className="reporte-clientes-porcentaje-badge completo">
                          100%
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Tabla de detalles */}
            <div className="reporte-clientes-tabla-container">
              <div className="reporte-clientes-tabla-header">
                <h3>
                  Detalle de horas
                  {(clienteSeleccionado || fechaInicio || fechaFin) && (
                    <span className="reporte-clientes-filtros-descripcion">
                      {clienteSeleccionado && ` - Cliente: ${clienteSeleccionado.label}`}
                      {fechaInicio && fechaFin && ` - Período: ${fechaInicio.toLocaleDateString()} a ${fechaFin.toLocaleDateString()}`}
                    </span>
                  )}
                </h3>
                <button 
                  className="reporte-clientes-btn-exportar"
                  onClick={exportarAExcel}
                >
                  Exportar detalle
                </button>
              </div>
              
              <div className="reporte-clientes-tabla-wrapper">
                <table className="reporte-clientes-tabla-detalle">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Cliente</th>
                      <th>Usuario</th>
                      <th>Seniority</th>
                      <th>Servicio</th>
                      <th>Tarea</th>
                      <th>Descripción</th>
                      <th>Horas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {horasConsultadas.map((hora, index) => (
                      <tr key={index}>
                        <td>{formatearFecha(hora.fechaCarga)}</td>
                        <td>{hora.cliente ? hora.cliente.nombre : 'Sin cliente'}</td>
                        <td>{hora.usuario ? `${hora.usuario.nombre} ${hora.usuario.apellidos}` : 'Usuario desconocido'}</td>
                        <td>{hora.senorityhora || 'N/A'}</td>
                        <td>{hora.servicio ? hora.servicio.nombre : 'Sin servicio'}</td>
                        <td>{hora.tarea ? hora.tarea.nombre : 'Sin tarea'}</td>
                        <td title={hora.descripcion}>
                          {hora.descripcion && hora.descripcion.length > 50 
                            ? hora.descripcion.substring(0, 50) + '...'
                            : hora.descripcion || 'Sin descripción'
                          }
                        </td>
                        <td className="reporte-clientes-horas-cell">{formatearHoras(hora.horas)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="reporte-clientes-horas-cell">
                        {formatearHoras(horasConsultadas.reduce((total, hora) => total + parseFloat(hora.horas), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {horasConsultadas.length === 0 && fechaInicio && fechaFin && (
          <div className="reporte-clientes-no-data">
            <h3>No se encontraron registros</h3>
            <p>No hay horas registradas para los filtros seleccionados.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReporteClientes;